/**
 * The `composition` block — how a medium is assembled, kept distinct from `frameModel`,
 * which is how it is *generated*.
 *
 * The whole block is optional, and its presence is a real product fact rather than a
 * convenience: the five digital platform plays never render to paper and carry none, film is
 * the one digital medium that does. `validateMedium` enforces both directions, so the toggle
 * here can be a plain switch and let the validator say when it is wrong.
 *
 * `pieces` and `outputs` are checkbox rows rather than a multi-select: there are four and ten
 * of them, and a list you can see all of is faster than a list you have to open.
 */

import { Checkbox, SelectField, Field, Toggle } from '@atlas-nook/design-system';
import type {
  CompositionInput,
  Layout,
  MediumComposition,
  OutputKind,
  PieceRole,
  RenderMode,
  SelectStrategy,
} from '@atlas-nook/mediums';
import type { JSX } from 'react';

import { ADMIN_STRINGS as S } from '../strings.js';
import {
  COMPOSITION_INPUT,
  entriesOf,
  LAYOUT,
  OUTPUT_KIND,
  PIECE_ROLE,
  RENDER_MODE,
  SELECT_STRATEGY,
} from './options.js';

/** What a medium gets when the switch is turned on: the commonest shape, ready to edit. */
export const DEFAULT_COMPOSITION: MediumComposition = {
  layout: 'sequence',
  input: 'flat',
  render: 'full-colour',
  scenes: { min: 1, max: 8, ideal: 6 },
  outputs: ['page'],
};

function toggleIn<T>(list: T[] | undefined, value: T, on: boolean): T[] {
  const set = new Set(list ?? []);
  if (on) set.add(value);
  else set.delete(value);
  return [...set];
}

export function CompositionFields({
  composition,
  onChange,
}: {
  composition: MediumComposition | undefined;
  onChange: (next: MediumComposition | undefined) => void;
}): JSX.Element {
  const has = composition !== undefined;

  function patch(next: Partial<MediumComposition>): void {
    if (composition === undefined) return;
    onChange({ ...composition, ...next });
  }

  return (
    <div className="stack">
      <Toggle
        label={S.medium.hasComposition}
        checked={has}
        onChange={(on) => onChange(on ? DEFAULT_COMPOSITION : undefined)}
      />
      <p className="note">{S.medium.hasCompositionHelp}</p>

      {composition !== undefined && (
        <>
          <div className="frow">
            <SelectField
              label={S.medium.layout}
              value={composition.layout}
              onChange={(value) => patch({ layout: value as Layout })}
            >
              {entriesOf(LAYOUT).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>

            <SelectField
              label={S.medium.input}
              value={composition.input}
              onChange={(value) => patch({ input: value as CompositionInput })}
            >
              {entriesOf(COMPOSITION_INPUT).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>

            <SelectField
              label={S.medium.render}
              value={composition.render}
              onChange={(value) => patch({ render: value as RenderMode })}
            >
              {entriesOf(RENDER_MODE).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="frow">
            <Field
              label={S.medium.scenesMin}
              type="number"
              value={String(composition.scenes.min)}
              onChange={(value) =>
                patch({ scenes: { ...composition.scenes, min: Number(value) || 0 } })
              }
            />
            <Field
              label={S.medium.scenesIdeal}
              type="number"
              value={String(composition.scenes.ideal)}
              onChange={(value) =>
                patch({ scenes: { ...composition.scenes, ideal: Number(value) || 0 } })
              }
            />
            <Field
              label={S.medium.scenesMax}
              type="number"
              value={String(composition.scenes.max)}
              onChange={(value) =>
                patch({ scenes: { ...composition.scenes, max: Number(value) || 0 } })
              }
            />
            <SelectField
              label={S.medium.selectStrategy}
              value={composition.select ?? ''}
              onChange={(value) => {
                const next = { ...composition };
                if (value === '') delete next.select;
                else next.select = value as SelectStrategy;
                onChange(next);
              }}
            >
              <option value="">{S.common.notSet}</option>
              {entriesOf(SELECT_STRATEGY).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </div>

          <fieldset className="fset">
            <legend className="lbl">{S.medium.pieces}</legend>
            <div className="chkrow">
              {entriesOf(PIECE_ROLE).map(([value, label]) => (
                <Checkbox
                  key={value}
                  label={label}
                  checked={(composition.pieces ?? []).includes(value)}
                  onChange={(on) => {
                    const pieces = toggleIn<PieceRole>(composition.pieces, value, on);
                    const next = { ...composition };
                    if (pieces.length === 0) delete next.pieces;
                    else next.pieces = pieces;
                    onChange(next);
                  }}
                />
              ))}
            </div>
            <p className="note">{S.medium.piecesHelp}</p>
          </fieldset>

          <fieldset className="fset">
            <legend className="lbl">{S.medium.outputs}</legend>
            <div className="chkrow">
              {entriesOf(OUTPUT_KIND).map(([value, label]) => (
                <Checkbox
                  key={value}
                  label={label}
                  checked={composition.outputs.includes(value)}
                  onChange={(on) =>
                    patch({ outputs: toggleIn<OutputKind>(composition.outputs, value, on) })
                  }
                />
              ))}
            </div>
          </fieldset>
        </>
      )}
    </div>
  );
}
